import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setAlertError, setAlertInfo, setAlertSuccess } from '../alert/alertSlice';
import workspaceService from './workspaceService';

const getErrorMessage = (error) =>
  error?.response?.data || error?.message || 'Workspace request failed.';

export const getWorkspaces = createAsyncThunk('workspace/getWorkspaces', async (_, thunkAPI) => {
  return await workspaceService
    .getAll()
    .then((response) => response)
    .catch((error) => thunkAPI.rejectWithValue(getErrorMessage(error)));
});

export const setActiveWorkspace = createAsyncThunk(
  'workspace/setActiveWorkspace',
  async (workspaceId, thunkAPI) => {
    return await workspaceService
      .setActive(workspaceId)
      .then((response) => response)
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const createWorkspace = createAsyncThunk(
  'workspace/createWorkspace',
  async (name, thunkAPI) => {
    return await workspaceService
      .create(name)
      .then((response) => {
        thunkAPI.dispatch(setAlertSuccess('Workspace created.'));
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const renameWorkspace = createAsyncThunk(
  'workspace/renameWorkspace',
  async ({ workspaceId, name }, thunkAPI) => {
    return await workspaceService
      .rename(workspaceId, name)
      .then((response) => {
        thunkAPI.dispatch(setAlertSuccess('Workspace updated.'));
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const addWorkspaceMember = createAsyncThunk(
  'workspace/addWorkspaceMember',
  async ({ workspaceId, member, role, inviteName, inviteEmail }, thunkAPI) => {
    return await workspaceService
      .addMember(workspaceId, member, role, inviteName, inviteEmail)
      .then((response) => {
        thunkAPI.dispatch(setAlertInfo('Workspace invite sent.'));
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const removeWorkspaceMember = createAsyncThunk(
  'workspace/removeWorkspaceMember',
  async ({ workspaceId, memberId, email, inviteId, pending = false }, thunkAPI) => {
    return await workspaceService
      .removeMember(workspaceId, memberId, email, inviteId, pending)
      .then((response) => {
        thunkAPI.dispatch(
          setAlertInfo(pending ? 'Invite deleted.' : 'Member removed from workspace.')
        );
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const updateWorkspaceMemberRole = createAsyncThunk(
  'workspace/updateWorkspaceMemberRole',
  async ({ workspaceId, memberId, role }, thunkAPI) => {
    return await workspaceService
      .updateMemberRole(workspaceId, memberId, role)
      .then((response) => {
        thunkAPI.dispatch(setAlertSuccess('Member role updated.'));
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

export const createInitialWorkspace = createAsyncThunk(
  'workspace/createInitialWorkspace',
  async (name, thunkAPI) => {
    return await workspaceService
      .createInitial(name)
      .then((response) => {
        thunkAPI.dispatch(setAlertSuccess('Workspace created.'));
        return response;
      })
      .catch((error) => {
        const err = getErrorMessage(error);
        thunkAPI.dispatch(setAlertError(err));
        return thunkAPI.rejectWithValue(err);
      });
  }
);

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState: {
    workspaces: [],
    currentWorkspace: null,
    isLoading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workspaces = action.payload;
        state.currentWorkspace = action.payload[0] || null;
      })
      .addCase(getWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.workspaces = [];
        state.currentWorkspace = null;
        state.error = action.payload || null;
      })
      .addCase(setActiveWorkspace.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(setActiveWorkspace.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkspace = action.payload;
      })
      .addCase(setActiveWorkspace.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(createWorkspace.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWorkspace.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkspace = action.payload;
        state.workspaces = state.workspaces.some((workspace) => workspace.id === action.payload.id)
          ? state.workspaces.map((workspace) =>
              workspace.id === action.payload.id ? action.payload : workspace
            )
          : [...state.workspaces, action.payload];
      })
      .addCase(createWorkspace.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(renameWorkspace.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(renameWorkspace.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkspace = action.payload;
        state.workspaces = state.workspaces.map((workspace) =>
          workspace.id === action.payload.id ? action.payload : workspace
        );
      })
      .addCase(renameWorkspace.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(addWorkspaceMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addWorkspaceMember.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addWorkspaceMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(removeWorkspaceMember.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(removeWorkspaceMember.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(removeWorkspaceMember.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(updateWorkspaceMemberRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWorkspaceMemberRole.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateWorkspaceMemberRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      })
      .addCase(createInitialWorkspace.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createInitialWorkspace.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkspace = action.payload;
        state.workspaces = state.workspaces.some((workspace) => workspace.id === action.payload.id)
          ? state.workspaces.map((workspace) =>
              workspace.id === action.payload.id ? action.payload : workspace
            )
          : [...state.workspaces, action.payload];
      })
      .addCase(createInitialWorkspace.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || null;
      });
  }
});

export default workspaceSlice.reducer;
